import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Référentiels système (spec 23 → 27)
// Statuts · Numérotation · Devises · Pays/Localisation · Langues
// Ces briques centrales sont consommées par tous les modules.
// On réutilise l'existant (monaie, pays, languages, translations)
// et on n'ajoute que ce qui manque (statuts, numérotation).
// ─────────────────────────────────────────────────────────────

// ═════════════════════════════════════════════════════════════
// 23. STATUTS — système standard + statuts métier par module
// ═════════════════════════════════════════════════════════════

export interface CoreStatus {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  scope: string;
  code: string;
  label: string;
  color: string | null;
  is_system: boolean;
  sort_order: number | null;
  created_at: string | null;
}

/** Couleurs adossées aux rôles StyleSystem. */
export const STATUS_COLORS: { key: string; label: string; className: string }[] = [
  { key: 'primary', label: 'Primaire', className: 'bg-primary-100 text-primary-900 border-primary-200' },
  { key: 'accent', label: 'Accent', className: 'bg-accent-100 text-accent-900 border-accent-200' },
  { key: 'secondary', label: 'Secondaire', className: 'bg-secondary-100 text-secondary-900 border-secondary-200' },
  { key: 'foreground', label: 'Neutre', className: 'bg-foreground-100 text-foreground-700 border-foreground-200' },
];

export function statusColorClass(key: string | null | undefined): string {
  return (STATUS_COLORS.find((c) => c.key === key) || STATUS_COLORS[2]).className;
}

/** Portées : chaque module peut définir ses propres statuts métier. */
export const STATUS_SCOPES: { key: string; label: string; icon: string }[] = [
  { key: 'generic', label: 'Générique', icon: 'ri-shield-check-line' },
  { key: 'product', label: 'Produit', icon: 'ri-shopping-bag-line' },
  { key: 'service', label: 'Service', icon: 'ri-service-line' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-cart-line' },
  { key: 'invoice', label: 'Facture', icon: 'ri-file-list-3-line' },
  { key: 'reservation', label: 'Réservation', icon: 'ri-calendar-check-line' },
  { key: 'employee', label: 'Employé', icon: 'ri-team-line' },
  { key: 'project', label: 'Projet', icon: 'ri-folders-line' },
];

export function statusScopeMeta(key: string | null | undefined) {
  return STATUS_SCOPES.find((s) => s.key === key) || STATUS_SCOPES[0];
}

export async function listCoreStatuses(organizationId: number): Promise<CoreStatus[]> {
  const { data, error } = await supabase
    .from('core_statuses')
    .select('*')
    .or(`organization_id.is.null,organization_id.eq.${organizationId}`)
    .order('scope', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreStatus[];
}

export async function createCoreStatus(input: {
  tenantId: number | null;
  organizationId: number;
  scope: string;
  code: string;
  label: string;
  color?: string;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_statuses')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      scope: input.scope,
      code: input.code,
      label: input.label,
      color: input.color || 'secondary',
      is_system: false,
      sort_order: 99,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreStatus(
  id: number,
  patch: { label?: string; color?: string; scope?: string; code?: string },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.label !== undefined) payload.label = patch.label;
  if (patch.color !== undefined) payload.color = patch.color;
  if (patch.scope !== undefined) payload.scope = patch.scope;
  if (patch.code !== undefined) payload.code = patch.code;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('core_statuses').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreStatus(id: number): Promise<void> {
  const { error } = await supabase.from('core_statuses').delete().eq('id', id);
  if (error) throw error;
}

// ═════════════════════════════════════════════════════════════
// 24. NUMÉROTATION — préfixes, séquence, padding, année, reset
// ═════════════════════════════════════════════════════════════

export interface CoreNumbering {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  entity_type: string;
  label: string | null;
  prefix: string;
  sequence: number;
  padding: number | null;
  year: number | null;
  reset_frequency: string | null;
  last_reset_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const NUMBERING_ENTITY_TYPES: { key: string; label: string; prefix: string; icon: string }[] = [
  { key: 'devis', label: 'Devis', prefix: 'DEV', icon: 'ri-file-text-line' },
  { key: 'invoice', label: 'Facture', prefix: 'FAC', icon: 'ri-file-list-3-line' },
  { key: 'order', label: 'Commande', prefix: 'CMD', icon: 'ri-shopping-cart-line' },
  { key: 'reservation', label: 'Réservation', prefix: 'RES', icon: 'ri-calendar-check-line' },
  { key: 'document', label: 'Document', prefix: 'DOC', icon: 'ri-file-3-line' },
  { key: 'ticket', label: 'Ticket', prefix: 'TCK', icon: 'ri-coupon-3-line' },
];

export const RESET_FREQUENCIES: { key: string; label: string }[] = [
  { key: 'never', label: 'Jamais' },
  { key: 'yearly', label: 'Chaque année' },
  { key: 'monthly', label: 'Chaque mois' },
  { key: 'daily', label: 'Chaque jour' },
];

export function resetFrequencyMeta(key: string | null | undefined) {
  return RESET_FREQUENCIES.find((r) => r.key === key) || RESET_FREQUENCIES[1];
}

/** Formate un numéro : PREFIX-ANNÉE-00001 (année et padding optionnels). */
export function formatNumber(prefix: string, sequence: number, padding: number | null, year: number | null): string {
  const pad = Math.max(1, Number(padding ?? 5));
  const seq = String(Math.max(0, Math.floor(sequence))).padStart(pad, '0');
  const parts = [prefix || 'DOC'];
  if (year) parts.push(String(year));
  parts.push(seq);
  return parts.join('-');
}

/** Aperçu du prochain numéro sans consommer la séquence. */
export function previewNumber(config: Pick<CoreNumbering, 'prefix' | 'sequence' | 'padding' | 'year'>): string {
  return formatNumber(config.prefix, (Number(config.sequence) || 0) + 1, config.padding, config.year);
}

export async function listCoreNumbering(organizationId: number): Promise<CoreNumbering[]> {
  const { data, error } = await supabase
    .from('core_numbering')
    .select('*')
    .eq('organization_id', organizationId)
    .order('entity_type', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreNumbering[];
}

export async function createCoreNumbering(input: {
  tenantId: number | null;
  organizationId: number;
  entityType: string;
  label?: string;
  prefix: string;
  padding: number;
  resetFrequency: string;
  useYear: boolean;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_numbering')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      entity_type: input.entityType,
      label: input.label || null,
      prefix: input.prefix,
      sequence: 0,
      padding: input.padding,
      year: input.useYear ? new Date().getFullYear() : null,
      reset_frequency: input.resetFrequency,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreNumbering(
  id: number,
  patch: { prefix?: string; padding?: number; resetFrequency?: string; label?: string; useYear?: boolean; sequence?: number },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.prefix !== undefined) payload.prefix = patch.prefix;
  if (patch.padding !== undefined) payload.padding = patch.padding;
  if (patch.resetFrequency !== undefined) payload.reset_frequency = patch.resetFrequency;
  if (patch.label !== undefined) payload.label = patch.label;
  if (patch.sequence !== undefined) payload.sequence = patch.sequence;
  if (patch.useYear !== undefined) payload.year = patch.useYear ? new Date().getFullYear() : null;
  const { error } = await supabase.from('core_numbering').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteCoreNumbering(id: number): Promise<void> {
  const { error } = await supabase.from('core_numbering').delete().eq('id', id);
  if (error) throw error;
}

/** Consomme la séquence et renvoie le numéro formaté (usage atomique côté serveur non garanti ici). */
export async function consumeNextNumber(config: CoreNumbering): Promise<string> {
  const next = (Number(config.sequence) || 0) + 1;
  const year = config.year ?? null;
  const { error } = await supabase
    .from('core_numbering')
    .update({ sequence: next, updated_at: new Date().toISOString() })
    .eq('id', config.id);
  if (error) throw error;
  return formatNumber(config.prefix, next, config.padding, year);
}

// ═════════════════════════════════════════════════════════════
// 25. DEVISES — devise principale par entreprise + multi-devises
// ═════════════════════════════════════════════════════════════

export interface CoreCurrency {
  id: number;
  code: string;
  nom: string;
  symbole: string | null;
  precision: number | null;
  symbol_position: string | null;
  is_active: number | null;
  exchange_rate: number | null;
  updated_at: string | null;
}

export const SYMBOL_POSITIONS: { key: string; label: string }[] = [
  { key: 'before', label: 'Avant (10 €)' },
  { key: 'after', label: 'Après (€ 10)' },
];

export interface CoreCurrencySettings {
  id: number | null;
  organization_id: number;
  primary_currency_code: string;
}

export async function listCoreCurrencies(): Promise<CoreCurrency[]> {
  const { data, error } = await supabase.from('monaie').select('*').order('code', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreCurrency[];
}

export async function updateCoreCurrency(
  id: number,
  patch: { is_active?: number; precision?: number; symbol_position?: string; exchange_rate?: number; symbole?: string; nom?: string },
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.is_active !== undefined) payload.is_active = patch.is_active;
  if (patch.precision !== undefined) payload.precision = patch.precision;
  if (patch.symbol_position !== undefined) payload.symbol_position = patch.symbol_position;
  if (patch.exchange_rate !== undefined) payload.exchange_rate = patch.exchange_rate;
  if (patch.symbole !== undefined) payload.symbole = patch.symbole;
  if (patch.nom !== undefined) payload.nom = patch.nom;
  const { error } = await supabase.from('monaie').update(payload).eq('id', id);
  if (error) throw error;
}

export async function getCurrencySettings(organizationId: number): Promise<CoreCurrencySettings> {
  const { data } = await supabase
    .from('core_currency_settings')
    .select('*')
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (data) return data as CoreCurrencySettings;
  return { id: null, organization_id: organizationId, primary_currency_code: 'EUR' };
}

export async function setPrimaryCurrency(organizationId: number, code: string): Promise<void> {
  const { error } = await supabase
    .from('core_currency_settings')
    .upsert(
      { organization_id: organizationId, primary_currency_code: code, updated_at: new Date().toISOString() },
      { onConflict: 'organization_id' },
    );
  if (error) throw error;
}

// ═════════════════════════════════════════════════════════════
// 26. PAYS ET LOCALISATION — informations pays (pas les règles fiscales)
// ═════════════════════════════════════════════════════════════

export interface CoreCountry {
  id: number;
  nom_pays: string;
  codepays: number | null;
  iso2: string | null;
  iso3: string | null;
  currency_code: string | null;
  region: string | null;
  locale: string | null;
  is_active: number | null;
}

export async function listCoreCountries(): Promise<CoreCountry[]> {
  const { data, error } = await supabase
    .from('pays')
    .select('*')
    .order('nom_pays', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreCountry[];
}

export async function updateCoreCountry(
  id: number,
  patch: { currency_code?: string | null; region?: string | null; locale?: string | null; iso2?: string | null; iso3?: string | null; is_active?: number },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.currency_code !== undefined) payload.currency_code = patch.currency_code || null;
  if (patch.region !== undefined) payload.region = patch.region || null;
  if (patch.locale !== undefined) payload.locale = patch.locale || null;
  if (patch.iso2 !== undefined) payload.iso2 = patch.iso2 || null;
  if (patch.iso3 !== undefined) payload.iso3 = patch.iso3 || null;
  if (patch.is_active !== undefined) payload.is_active = patch.is_active;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('pays').update(payload).eq('id', id);
  if (error) throw error;
}

// ═════════════════════════════════════════════════════════════
// 27. LANGUES — internationalisation + versions traduisibles
// ═════════════════════════════════════════════════════════════

export interface CoreLanguage {
  id: number;
  code: string;
  label: string;
  native_label: string | null;
  flag: string | null;
  dir: string | null;
  active: number | null;
  is_default: number | null;
  sort_order: number | null;
}

export async function listCoreLanguages(): Promise<CoreLanguage[]> {
  const { data, error } = await supabase
    .from('languages')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreLanguage[];
}

export async function createCoreLanguage(input: {
  code: string;
  label: string;
  nativeLabel?: string;
  flag?: string;
  dir?: string;
}): Promise<number | null> {
  const { data: maxRow } = await supabase
    .from('languages')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = (Number((maxRow as { sort_order?: number } | null)?.sort_order) || 0) + 1;
  const { data, error } = await supabase
    .from('languages')
    .insert({
      code: input.code,
      label: input.label,
      native_label: input.nativeLabel || input.label,
      flag: input.flag || '',
      dir: input.dir || 'ltr',
      active: 1,
      is_default: 0,
      sort_order: nextOrder,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function updateCoreLanguage(
  id: number,
  patch: { label?: string; nativeLabel?: string; flag?: string; dir?: string; active?: number },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.label !== undefined) payload.label = patch.label;
  if (patch.nativeLabel !== undefined) payload.native_label = patch.nativeLabel;
  if (patch.flag !== undefined) payload.flag = patch.flag;
  if (patch.dir !== undefined) payload.dir = patch.dir;
  if (patch.active !== undefined) payload.active = patch.active;
  if (Object.keys(payload).length === 0) return;
  const { error } = await supabase.from('languages').update(payload).eq('id', id);
  if (error) throw error;
}

/** Définit la langue par défaut (exclusive). */
export async function setDefaultLanguage(id: number): Promise<void> {
  await supabase.from('languages').update({ is_default: 0 }).neq('id', id);
  const { error } = await supabase.from('languages').update({ is_default: 1, active: 1 }).eq('id', id);
  if (error) throw error;
}

/**
 * Version traduisible d'une donnée (spec 27) : { fr: "...", en: "...", ar: "..." }.
 * Permet de ne pas dupliquer l'entité entière pour chaque langue.
 */
export type TranslationMap = Record<string, string>;

export function translationMapToText(map: TranslationMap | null | undefined): string {
  if (!map) return '';
  const entries = Object.entries(map).filter(([, v]) => (v || '').trim());
  if (entries.length === 0) return '';
  return entries.map(([k, v]) => `${k}: ${v}`).join(' · ');
}

// ═════════════════════════════════════════════════════════════
// 28. TIMEZONE — société · établissement · utilisateur
// Les dates sont stockées de façon cohérente (UTC) puis affichées
// selon le fuseau approprié. Essentiel pour RDV, réservations,
// hôtels, activités, événements et notifications.
// ═════════════════════════════════════════════════════════════

export interface CoreTimezone {
  code: string;
  label: string;
  region: string;
}

/** Fuseaux courants (Afrique, Europe, Amériques, Moyen-Orient). */
export const CORE_TIMEZONES: CoreTimezone[] = [
  { code: 'UTC', label: 'UTC — Temps universel', region: 'Universel' },
  { code: 'Africa/Casablanca', label: 'Casablanca (Maroc)', region: 'Afrique' },
  { code: 'Africa/Algiers', label: 'Alger (Algérie)', region: 'Afrique' },
  { code: 'Africa/Tunis', label: 'Tunis (Tunisie)', region: 'Afrique' },
  { code: 'Africa/Dakar', label: 'Dakar (Sénégal)', region: 'Afrique' },
  { code: 'Africa/Abidjan', label: 'Abidjan (Côte d’Ivoire)', region: 'Afrique' },
  { code: 'Africa/Lagos', label: 'Lagos (Nigéria)', region: 'Afrique' },
  { code: 'Africa/Douala', label: 'Douala (Cameroun)', region: 'Afrique' },
  { code: 'Africa/Porto-Novo', label: 'Porto-Novo (Bénin)', region: 'Afrique' },
  { code: 'Africa/Lome', label: 'Lomé (Togo)', region: 'Afrique' },
  { code: 'Africa/Libreville', label: 'Libreville (Gabon)', region: 'Afrique' },
  { code: 'Africa/Kinshasa', label: 'Kinshasa (RDC)', region: 'Afrique' },
  { code: 'Africa/Conakry', label: 'Conakry (Guinée)', region: 'Afrique' },
  { code: 'Africa/Nairobi', label: 'Nairobi (Kenya)', region: 'Afrique' },
  { code: 'Africa/Johannesburg', label: 'Johannesburg (Afrique du Sud)', region: 'Afrique' },
  { code: 'Europe/Paris', label: 'Paris (France)', region: 'Europe' },
  { code: 'Europe/Brussels', label: 'Bruxelles (Belgique)', region: 'Europe' },
  { code: 'Europe/Madrid', label: 'Madrid (Espagne)', region: 'Europe' },
  { code: 'Europe/Lisbon', label: 'Lisbonne (Portugal)', region: 'Europe' },
  { code: 'Europe/Berlin', label: 'Berlin (Allemagne)', region: 'Europe' },
  { code: 'Europe/London', label: 'Londres (Royaume-Uni)', region: 'Europe' },
  { code: 'America/New_York', label: 'New York (USA)', region: 'Amériques' },
  { code: 'America/Toronto', label: 'Toronto (Canada)', region: 'Amériques' },
  { code: 'Asia/Dubai', label: 'Dubaï (Émirats)', region: 'Moyen-Orient' },
];

export function timezoneMeta(code: string | null | undefined): CoreTimezone {
  return CORE_TIMEZONES.find((t) => t.code === code) || CORE_TIMEZONES[0];
}

/** Décale une date vers le fuseau cible et la formate. */
export function formatInTimezone(
  date: string | Date | null | undefined,
  timezone: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleString('fr-FR', {
      timeZone: timezone || 'UTC',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...opts,
    });
  } catch {
    return d.toLocaleString('fr-FR', opts);
  }
}

export interface CoreTimezoneSettings {
  tenant_timezone: string | null;
  organization_timezone: string | null;
}

export async function getOrganizationTimezones(orgId: number): Promise<CoreTimezoneSettings> {
  const { data: org } = await supabase
    .from('core_organizations')
    .select('timezone, tenant_id')
    .eq('id', orgId)
    .maybeSingle();
  let tenantTz: string | null = null;
  const tenantId = (org as { tenant_id?: number } | null)?.tenant_id;
  if (tenantId) {
    const { data: tenant } = await supabase
      .from('core_tenants')
      .select('timezone')
      .eq('id', tenantId)
      .maybeSingle();
    tenantTz = (tenant as { timezone?: string } | null)?.timezone ?? null;
  }
  return {
    tenant_timezone: tenantTz,
    organization_timezone: (org as { timezone?: string } | null)?.timezone ?? null,
  };
}

export async function setOrganizationTimezone(orgId: number, timezone: string): Promise<void> {
  const { error } = await supabase.from('core_organizations').update({ timezone }).eq('id', orgId);
  if (error) throw error;
}

export async function setTenantTimezone(tenantId: number, timezone: string): Promise<void> {
  const { error } = await supabase.from('core_tenants').update({ timezone }).eq('id', tenantId);
  if (error) throw error;
}

export interface CoreLocationTimezone {
  id: number;
  name: string;
  is_primary: boolean | null;
  timezone: string | null;
}

export async function listLocationTimezones(orgId: number): Promise<CoreLocationTimezone[]> {
  const { data, error } = await supabase
    .from('core_locations')
    .select('id, name, is_primary, timezone')
    .eq('organization_id', orgId)
    .order('is_primary', { ascending: false })
    .order('id', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreLocationTimezone[];
}

export async function setLocationTimezone(locationId: number, timezone: string): Promise<void> {
  const { error } = await supabase.from('core_locations').update({ timezone }).eq('id', locationId);
  if (error) throw error;
}

export async function getUserTimezone(userId: number): Promise<string | null> {
  const { data } = await supabase.from('users').select('timezone').eq('id', userId).maybeSingle();
  return (data as { timezone?: string } | null)?.timezone ?? null;
}

export async function setUserTimezone(userId: number, timezone: string): Promise<void> {
  const { error } = await supabase.from('users').update({ timezone }).eq('id', userId);
  if (error) throw error;
}

/**
 * Réservation d'un numéro central (spec 24) : trouve la config de
 * l'organisation pour le type d'entité, consomme la séquence et renvoie
 * le numéro formaté (ex : FAC-2026-00001). Renvoie null si non configuré.
 */
export async function reserveEntityNumber(
  organizationId: number,
  entityType: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('core_numbering')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('entity_type', entityType)
    .maybeSingle();
  if (error || !data) return null;
  const config = data as CoreNumbering;
  const year = config.reset_frequency === 'yearly' ? new Date().getFullYear() : config.year;
  return consumeNextNumber({ ...config, year });
}

/** Crée une config de numérotation par défaut si l'organisation n'en a aucune. */
export async function ensureNumberingConfig(input: {
  tenantId: number | null;
  organizationId: number;
  entityType: string;
  prefix: string;
  padding?: number;
  useYear?: boolean;
}): Promise<CoreNumbering | null> {
  const { data } = await supabase
    .from('core_numbering')
    .select('*')
    .eq('organization_id', input.organizationId)
    .eq('entity_type', input.entityType)
    .maybeSingle();
  if (data) return data as CoreNumbering;
  const id = await createCoreNumbering({
    tenantId: input.tenantId,
    organizationId: input.organizationId,
    entityType: input.entityType,
    prefix: input.prefix,
    padding: input.padding ?? 5,
    resetFrequency: 'yearly',
    useYear: input.useYear ?? true,
  });
  if (!id) return null;
  const { data: created } = await supabase.from('core_numbering').select('*').eq('id', id).maybeSingle();
  return (created as CoreNumbering) || null;
}